import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { ResponseContext } from "../../../@api";

import { TokenRefreshManager } from "../../../auth/TokenRefreshManager";
import { IMiddelware } from "../../models/interfaces/middelware.interface";
import { UserStore } from "../../stores";

const RETRY_MARKER_HEADER = "x-token-refresh-retry";
const RETRY_MARKER_VALUE = "1";

/**
 * TokenRefreshMiddleware handles HTTP 403 responses by attempting to refresh the access token.
 *
 * Behavior (Silent-First Strategy):
 * 1. Catches HTTP 403 responses from the backend
 * 2. Determines if the 403 is session-expired (not permission-related)
 * 3. Attempts silent token refresh using MSAL (no user interaction)
 * 4. If silent refresh succeeds:
 *    - Updates the access token in the store
 *    - Retries the original request transparently
 *    - No popup is shown to the user
 * 5. If silent refresh fails with InteractionRequiredAuthError:
 *    - Falls back to popup-based reauthentication
 *    - Only shown when Azure AD requires user interaction (MFA, consent, etc.)
 * 6. If popup fails:
 *    - Falls back to redirect-based token acquisition (absolute fallback)
 * 7. If all token acquisition attempts fail:
 *    - Returns the original 403 response
 *    - AuthorizationHandlingMiddleware displays appropriate error
 *
 * Notes:
 * - Only one token refresh happens at a time; queued requests wait for the result
 * - No forced popups; silently refreshed tokens avoid user interaction
 * - Does not distinguish between expired token and permission-related 403s
 *   (if it's a permission issue, retry will fail again with 403)
 * - Infinite retry loops are prevented with the retry marker header
 */
export class TokenRefreshMiddleware implements IMiddelware {
  private static popupReauthenticationPromise: Promise<string | null> | null = null;

  public readonly handledHttpStatusCodes = [403];

  constructor(private readonly userStore: UserStore) {}

  public setError(context: ResponseContext): void {
    void context;
  }

  public resetError(): void {}

  public async post(context: ResponseContext): Promise<Response | void> {
    if (context.response.status === 403 && !this.hasRetryMarker(context)) {
      return await this.handleTokenExpiry(context);
    }
  }

  private async handleTokenExpiry(context: ResponseContext): Promise<Response | void> {
    try {
      const isSessionExpired = await this.isSessionExpired403(context);
      if (!isSessionExpired) {
        return;
      }

      const tokenRefreshManager = TokenRefreshManager.getInstance();

      // Step 1: Attempt silent token refresh first (SILENT-FIRST strategy)
      // This avoids showing unnecessary popups if the token can be silently refreshed
      let newAccessToken: string | null = null;
      try {
        newAccessToken = await tokenRefreshManager.refreshAccessToken();
        if (newAccessToken) {
          console.debug("TokenRefreshMiddleware: Access token refreshed silently");
        }
      } catch (error) {
        // If silent refresh fails with InteractionRequired, proceed to popup fallback
        if (error instanceof InteractionRequiredAuthError) {
          console.debug(
            "TokenRefreshMiddleware: Silent token refresh requires interaction, attempting popup reauthentication",
          );
        } else {
          throw error;
        }
      }

      // Step 2: If silent refresh failed, use popup as fallback
      // Only shown when Azure AD requires user interaction (e.g., MFA, consent)
      if (!newAccessToken) {
        newAccessToken = await this.acquireTokenWithPopupQueue(tokenRefreshManager);
      }

      // Step 3: If popup also failed, use redirect as final fallback
      if (!newAccessToken) {
        console.warn("TokenRefreshMiddleware: Could not acquire new access token via popup reauthentication");
        await tokenRefreshManager.acquireTokenRedirectFallback();
        // Let the request fail - AuthorizationHandlingMiddleware will handle it
        return;
      }

      // Update the stored token
      this.userStore.setAccessToken(newAccessToken);
      console.debug("TokenRefreshMiddleware: Access token updated in store");

      // Retry the original request with the new token
      const retryResponse = await this.retryRequest(context, newAccessToken);

      // Replace the failed response with the retried response
      return retryResponse;
    } catch (error) {
      console.error("TokenRefreshMiddleware: Unexpected error during token refresh", error);
      // Let the request fail - AuthorizationHandlingMiddleware will handle it
    }
  }

  private async acquireTokenWithPopupQueue(tokenRefreshManager: TokenRefreshManager): Promise<string | null> {
    if (TokenRefreshMiddleware.popupReauthenticationPromise) {
      return await TokenRefreshMiddleware.popupReauthenticationPromise;
    }

    TokenRefreshMiddleware.popupReauthenticationPromise = (async () => {
      try {
        console.debug("TokenRefreshMiddleware: Attempting popup reauthentication");
        return await tokenRefreshManager.acquireTokenInteractive();
      } finally {
        TokenRefreshMiddleware.popupReauthenticationPromise = null;
      }
    })();

    return await TokenRefreshMiddleware.popupReauthenticationPromise;
  }

  private hasRetryMarker(context: ResponseContext): boolean {
    const headers = new Headers(context.init?.headers);
    return headers.get(RETRY_MARKER_HEADER) === RETRY_MARKER_VALUE;
  }

  private async isSessionExpired403(context: ResponseContext): Promise<boolean> {
    const authHeader = context.response.headers.get("www-authenticate")?.toLowerCase() ?? "";
    if (authHeader.includes("invalid_token") || authHeader.includes("expired")) {
      return true;
    }

    const responseClone = context.response.clone();
    let payloadText = "";

    try {
      const body = (await responseClone.json()) as Record<string, unknown>;
      const title = String(body.title ?? "").toLowerCase();
      const detail = String(body.detail ?? "").toLowerCase();
      const type = String(body.type ?? "").toLowerCase();
      const code = String(body.code ?? body.errorCode ?? body.error ?? "").toLowerCase();

      payloadText = [title, detail, type, code].join(" ");
    } catch {
      try {
        payloadText = (await responseClone.text()).toLowerCase();
      } catch {
        payloadText = "";
      }
    }

    if (!payloadText) {
      return false;
    }

    const permissionSignals = [
      "insufficient permission",
      "insufficient permissions",
      "insufficient privileges",
      "permission",
      "forbidden",
      "access denied",
      "not authorized",
    ];

    if (permissionSignals.some(signal => payloadText.includes(signal))) {
      return false;
    }

    const sessionExpiredSignals = [
      "session expired",
      "expired session",
      "token expired",
      "invalid token",
      "invalid_token",
      "login required",
      "reauth",
      "unauthenticated",
      "authentication required",
      "interaction required",
      "consent required",
      "token_expired",
      "session_expired",
    ];

    return sessionExpiredSignals.some(signal => payloadText.includes(signal));
  }

  private async retryRequest(context: ResponseContext, newAccessToken: string): Promise<Response> {
    const headers = new Headers(context.init?.headers);
    headers.set("Authorization", `Bearer ${newAccessToken}`);
    headers.set(RETRY_MARKER_HEADER, RETRY_MARKER_VALUE);

    // Clone the original request init and update the authorization header
    const retryInit: RequestInit = {
      ...context.init,
      headers,
    };

    try {
      console.debug("TokenRefreshMiddleware: Retrying request with new token");
      const response = await fetch(context.url, retryInit);
      return response;
    } catch (error) {
      console.error("TokenRefreshMiddleware: Retry request failed", error);
      // Return the original failed response
      return context.response;
    }
  }
}
